import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Button, Form, Icon, Label, Message, Modal, Table,
} from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';

import { fetchM } from '../../actions/fetchM';
import IntegrationStatusTile from './IntegrationStatusTile';
import { newRequestGuard } from '../../utils/crmLink';
import { providerLabel } from '../../utils/crmContext';
import {
  blockingRow, canResolve, eventLabel, formatWhen, isNonTerminal, lastAnswer, retryableIDs,
  sortRows, statusChip,
} from '../../utils/crmSync';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';
import '../../styles/components/crmSyncPanel.less';

// The CRM sync panel on the booking overview (MS-6629, T6.4): every outbox row for the booking in
// plain words, its state, attempts, the provider's last answer as codes with the instruction for a
// parked row, timestamps, and — for admins — Retry and Mark resolved, wired to MS-6615's API.
//
// What it will not show: the request, the response or the provider's verbatim message. Those are
// personal data and are not on this view (MS-6646); the audited raw route exists for MHub staff and
// nothing here calls it. The server masks at source; this panel adds no field of its own.
//
// Retry is enabled only where the server would accept it (the oldest parked row of its provider),
// so the button never promises what a 409 then refuses. Resolve asks for its note first, because
// the server requires one. After either action the list is re-read; while any row is still moving
// it is re-read every POLL_MS on its own, so a salesperson watching a booking go out sees it land.
export const POLL_MS = 30000;
const ns = { ns: TRANSLATION_FILES.translation };

export class CrmSyncPanel extends Component {
  static propTypes = {
    bookingID: PropTypes.string,
    token: PropTypes.string,
    // canAct: both actions (kept for callers that have not split them). canRetry / canResolve
    // mirror the route ACL (MS-6748): Retry is open to admins and Developer Executives, Mark
    // resolved to admins only. Either explicit prop wins over canAct.
    canAct: PropTypes.bool,
    canRetry: PropTypes.bool,
    canResolve: PropTypes.bool,
    dispatch: PropTypes.func.isRequired,
    // Optional layout around the panel, applied only when there is something to show: a parent that
    // sits the panel in a grid row must not be left with an empty, padded row when it is hidden.
    wrap: PropTypes.func,
    compact: PropTypes.bool,
    t: PropTypes.func.isRequired,
  }

  static defaultProps = {
    bookingID: '',
    token: '',
    canAct: false,
    canRetry: undefined,
    canResolve: undefined,
    wrap: null,
    compact: false,
  }

  // A change of booking resets everything booking-scoped before the next render, so nothing of the
  // previous booking is ever actionable while the next one loads (getDerivedStateFromProps is the
  // React 16 way to reset state from a prop; the mount site also keys the panel by booking).
  static getDerivedStateFromProps(props, state) {
    if (props.bookingID === state.forBooking) return null;
    return {
      forBooking: props.bookingID,
      historyOpen: false,
      rows: null,
      loading: false,
      error: '',
      actionError: '',
      acting: null,
      resolving: null,
      note: '',
    };
  }

  state = {
    historyOpen: false,
    forBooking: undefined, // the booking the state below is about
    rows: null, // null: never loaded; []: loaded, none
    loading: false,
    error: '', // the last load's failure, cleared by a good load
    actionError: '', // the last action's refusal, cleared by the next action or by Refresh
    acting: null, // row id an action is in flight for
    resolving: null, // row being resolved (modal open)
    note: '',
  };

  componentDidMount() {
    this.mounted = true;
    this.load();
  }

  componentDidUpdate(prev) {
    // State was already reset for the new booking (getDerivedStateFromProps); an action still in
    // flight for the old one abandons itself in act().
    if (prev.bookingID !== this.props.bookingID) this.load();
  }

  componentWillUnmount() {
    this.mounted = false;
    this.guard.invalidate();
    this.stopPolling();
  }

  // Set in componentDidMount and cleared in componentWillUnmount: an await can return after the
  // panel is gone, and neither setState nor a new read may follow it then.
  mounted = false;

  guard = newRequestGuard();

  timer = null;

  // The two rights, resolved once: an explicit prop wins, else canAct stands for both.
  rights() {
    const { canAct, canRetry, canResolve: canResolveProp } = this.props;
    return {
      retry: typeof canRetry === 'boolean' ? canRetry : Boolean(canAct),
      resolve: typeof canResolveProp === 'boolean' ? canResolveProp : Boolean(canAct),
    };
  }

  stopPolling() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  // Only the newest read may act (see newRequestGuard): the overview can move to another booking
  // while a read is in flight, and a late answer must not put another booking's rows here.
  load = async () => {
    if (!this.mounted) return;
    const token = this.guard.begin();
    this.stopPolling();
    const { bookingID } = this.props;
    if (!bookingID) {
      this.setState({ rows: [], loading: false });
      return;
    }
    this.setState({ loading: true });
    let res;
    try {
      // returnForbidden / ignoreNotFound: fetchM otherwise navigates the whole page to /forbidden/
      // or /not_found/. A salesperson who may see the booking but not the sync list is a 403 that
      // means "no panel", not "leave the page".
      res = await this.props.dispatch(fetchM(`/bookings/${encodeURIComponent(bookingID)}/crm/events/`, {
        credentials: 'include', returnForbidden: true, ignoreNotFound: true,
      }));
    } catch (err) {
      res = err && err.res;
    }
    if (!this.guard.isCurrent(token)) return;
    if (res && (res.status === 403 || res.status === 404)) {
      // Not allowed to see the list means no panel for this user (a refusal from an earlier action
      // goes with it). Whether the tenant uses a CRM at all is the overview's question, answered
      // from /app/ (MS-6631), never from this read.
      this.setState({
        rows: [], loading: false, error: '', actionError: '', resolving: null, note: '',
      });
      return;
    }
    let rows = null;
    if (res && res.ok) {
      try {
        rows = await res.json();
      } catch (e) {
        rows = null;
      }
    }
    if (!this.guard.isCurrent(token)) return;
    if (!Array.isArray(rows)) {
      // A failed read, or a body that is not the list: say so and keep what was on the screen. An
      // empty history is only ever a valid empty array. Rows still moving keep their re-read, else
      // one transient failure would end the polling for good.
      const previous = this.state.rows;
      this.setState({ loading: false, error: this.props.t('The CRM sync history could not be loaded.', ns) });
      if (!this.mounted || !this.guard.isCurrent(token)) return;
      if (previous && previous.some(isNonTerminal)) this.timer = setTimeout(this.load, POLL_MS);
      return;
    }
    rows = sortRows(rows);
    // A resolve modal open on a row that is no longer parked (a poll moved it) closes: the action
    // it offered is no longer one the server would accept.
    const { resolving } = this.state;
    const stillParked = resolving && rows.some(r => r.id === resolving.id && canResolve(r));
    this.setState({
      rows, loading: false, error: '', resolving: stillParked ? resolving : null, note: stillParked ? this.state.note : '',
    });
    // setState above can run a parent re-render synchronously that unmounts this panel; a timer set
    // now would outlive the component.
    if (!this.mounted || !this.guard.isCurrent(token)) return;
    if (rows.some(isNonTerminal)) {
      this.timer = setTimeout(this.load, POLL_MS);
    }
  }

  act = async (row, action, note) => {
    const { bookingID, token, t } = this.props;
    this.setState({ acting: row.id, actionError: '' });
    let res;
    try {
      // 403 (not an admin) and 409 (not parked / not the oldest) are this route's contract; the
      // panel shows their text, so fetchM must not redirect on them.
      res = await this.props.dispatch(fetchM(`/bookings/${encodeURIComponent(bookingID)}/crm/events/${row.id}/${action}/`, {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Csrf-Token': token },
        body: JSON.stringify({ note: note || '' }),
        returnForbidden: true,
        ignoreNotFound: true,
      }));
    } catch (err) {
      res = err && err.res;
    }
    // Gone, or about another booking by now: the answer is nobody's business here. The row's own
    // booking is what was posted, so nothing wrong was sent; only the screen must not react.
    if (!this.mounted || this.props.bookingID !== bookingID) return;
    let error = '';
    if (!res || !res.ok) {
      let text = '';
      try {
        text = res ? await res.text() : '';
      } catch (e) {
        text = '';
      }
      // Reading the body is another await; the same two things may have happened during it.
      if (!this.mounted || this.props.bookingID !== bookingID) return;
      error = text || t('The action could not be completed.', ns);
    }
    // The list is re-read whether the action succeeded or not: on a 409 the row has moved since
    // it was drawn, and the fresh list is the explanation — shown next to the refusal, which a good
    // re-read must not wipe. While that re-read is in flight `loading` keeps every action disabled,
    // so the stale rows still on the screen cannot take a second action against an ordering that
    // has already changed.
    this.setState({ acting: null, resolving: null, note: '', actionError: error });
    await this.load();
  }

  handleRetry = row => this.actFor(row, 'retry', '');

  // A guard for the actions too: the click is judged against the rows on the screen NOW, not the
  // ones it was drawn from. A row that a poll has since moved (sent, resolved, no longer the
  // oldest parked) gets no POST the server would refuse; a click after the booking changed finds
  // no such row at all.
  actFor = (row, action, note) => {
    const rows = this.state.rows || [];
    const current = row && rows.find(r => r.id === row.id);
    if (!current) return Promise.resolve();
    if (action === 'retry' && (!retryableIDs(rows)[current.id] || (current.event === 'contact_details_changed' && current.needsHumanReason === 'contact_ref_missing'))) return Promise.resolve();
    if (action === 'resolve' && !canResolve(current)) return Promise.resolve();
    return this.act(current, action, note);
  }

  handleOpenResolve = row => this.setState({ resolving: row, note: '', actionError: '' });

  handleRefresh = () => {
    this.setState({ actionError: '' });
    this.load();
  }

  handleCloseResolve = () => this.setState({ resolving: null, note: '' });

  handleNote = (e, data) => this.setState({ note: data.value });

  handleResolve = () => {
    const { resolving, note } = this.state;
    if (!resolving || !note.trim()) return;
    this.actFor(resolving, 'resolve', note.trim());
  }

  renderRow(row, retryable) {
    const { t } = this.props;
    const rights = this.rights();
    const canAct = rights.retry || rights.resolve;
    const { acting } = this.state;
    const chip = statusChip(row);
    const parked = canResolve(row);
    const blocker = blockingRow(row, this.state.rows || []);
    const busy = acting !== null || this.state.loading;
    const answer = lastAnswer(row, (k, o) => t(k, { ...ns, ...o }));
    // A disabled Retry always says why: the ordering rule, or that the list is being re-read.
    let retryTitle = '';
    if ((row.event === 'contact_details_changed' && row.needsHumanReason === 'contact_ref_missing')) retryTitle = row.instruction;
    else if (!retryable[row.id]) retryTitle = t('An older row of this booking is still pending or parked; retry that one first', ns);
    else if (busy) retryTitle = t('Please wait: the list is being re-read', ns);
    return (
      <Table.Row key={row.id} data-testid={`crm-sync-row-${row.id}`} data-status={row.status}>
        <Table.Cell>
          <div className="crm-sync-panel__event">{t(eventLabel(row.event), ns)}</div>
          <div className="crm-sync-panel__meta">{providerLabel(row.provider)} · #{row.seq}</div>
        </Table.Cell>
        <Table.Cell>
          <Label size="small" color={chip.color} data-testid={`crm-sync-status-${row.id}`}>{t(chip.label, ns)}</Label>
          {row.needsHumanReason && parked ? (
            <div className="crm-sync-panel__reason"><code>{row.needsHumanReason}</code></div>
          ) : null}
        </Table.Cell>
        <Table.Cell textAlign="center">{row.attempts || 0}</Table.Cell>
        <Table.Cell>
          {answer ? <div className="crm-sync-panel__codes">{answer}</div> : null}
          {blocker ? (
            <div className="crm-sync-panel__instruction" data-testid={`crm-sync-blocked-${row.id}`}>
              {t('Waiting for event #{{seq}}: {{event}} ({{status}}).', {
                ...ns, seq: blocker.seq, event: t(eventLabel(blocker.event), ns),
                status: t(statusChip(blocker).label, ns),
              })}
            </div>
          ) : null}
          {row.instruction && parked ? (
            <div className="crm-sync-panel__instruction" data-testid={`crm-sync-instruction-${row.id}`}>
              {row.instruction}
            </div>
          ) : null}
          {row.bodiesPurgedAt ? <div className="crm-sync-panel__meta">{t('Details erased by retention', ns)}</div> : null}
        </Table.Cell>
        <Table.Cell className="crm-sync-panel__when">
          {row.sentAt ? <div>{t('Sent', ns)} {formatWhen(row.sentAt)}</div> : null}
          {!row.sentAt && row.lastAttemptAt ? <div>{t('Last attempt', ns)} {formatWhen(row.lastAttemptAt)}</div> : null}
          {row.status === 'pending' && row.nextAttemptAt ? <div>{t('Next attempt', ns)} {formatWhen(row.nextAttemptAt)}</div> : null}
          <div className="crm-sync-panel__meta">{t('Queued', ns)} {formatWhen(row.createdAt)}</div>
        </Table.Cell>
        {canAct ? (
          <Table.Cell textAlign="right" className="crm-sync-panel__actions">
            {parked ? (
              <span>
                {rights.retry ? (
                  <Button
                    size="mini"
                    compact
                    disabled={!retryable[row.id] || busy || (row.event === 'contact_details_changed' && row.needsHumanReason === 'contact_ref_missing')}
                    loading={acting === row.id}
                    onClick={() => this.handleRetry(row)}
                    title={retryTitle}
                    data-testid={`crm-sync-retry-${row.id}`}
                  >
                    {t('Retry', ns)}
                  </Button>
                ) : null}
                {rights.resolve ? (
                  <Button
                    size="mini"
                    compact
                    disabled={busy}
                    onClick={() => this.handleOpenResolve(row)}
                    data-testid={`crm-sync-resolve-${row.id}`}
                  >
                    {t('Mark resolved', ns)}
                  </Button>
                ) : null}
              </span>
            ) : null}
          </Table.Cell>
        ) : null}
      </Table.Row>
    );
  }

  render() {
    const { t } = this.props;
    const rights = this.rights();
    const canAct = rights.retry || rights.resolve;
    const {
      rows, loading, error, actionError, resolving, note, acting,
    } = this.state;
    // Nothing to say until the first read answers, and nothing at all for a booking no CRM has
    // rows for: the panel's presence is what tells an IJM tenant's overview apart from an OP one.
    const problem = actionError || error;
    if (!this.props.compact) {
      if (rows === null && !problem) return null;
      if (rows && rows.length === 0 && !problem) return null;
    } else if (!problem && (rows === null || rows.length === 0)) {
      // The tile row (MS-6748) must not lose a column while the first read is out, nor when the
      // booking has no rows (made before the integration, or not queued yet): a disabled tile says
      // so, in the same frame, so its neighbour and the caption keep their place.
      const empty = (
        <span className="crm-sync-summary">
          <IntegrationStatusTile
            name={t('CRM sync', ns)}
            state={rows === null ? t('Loading', ns) : t('Not sent', ns)}
            tone={rows === null ? 'waiting' : 'closed'}
            icon="exchange"
            testId="crm-sync-open"
          />
        </span>
      );
      return this.props.wrap ? this.props.wrap(empty) : empty;
    }
    const retryable = retryableIDs(rows || []);
    const { wrap } = this.props;
    const content = (
      <div className="crm-sync-panel" data-testid="crm-sync-panel">
        <div className="crm-sync-panel__header">
          <b>{t('CRM sync', ns)}</b>
          <Button
            size="mini"
            compact
            basic
            icon
            loading={loading}
            disabled={loading}
            onClick={this.handleRefresh}
            aria-label={t('Refresh', ns)}
            data-testid="crm-sync-refresh"
          >
            <Icon name="refresh" /> {t('Refresh', ns)}
          </Button>
        </div>
        {problem ? <Message size="mini" negative data-testid="crm-sync-error">{problem}</Message> : null}
        {rows && rows.length > 0 ? (
          <div className="crm-sync-panel__table">
            <Table compact size="small" unstackable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t('Event', ns)}</Table.HeaderCell>
                  <Table.HeaderCell>{t('Status', ns)}</Table.HeaderCell>
                  <Table.HeaderCell textAlign="center">{t('Attempts', ns)}</Table.HeaderCell>
                  <Table.HeaderCell>{t('Last answer', ns)}</Table.HeaderCell>
                  <Table.HeaderCell>{t('When', ns)}</Table.HeaderCell>
                  {canAct ? <Table.HeaderCell /> : null}
                </Table.Row>
              </Table.Header>
              <Table.Body>{rows.map(r => this.renderRow(r, retryable))}</Table.Body>
            </Table>
          </div>
        ) : null}
        <Modal open={Boolean(resolving)} size="tiny" onClose={this.handleCloseResolve}>
          <Modal.Header>{t('Mark resolved', ns)}</Modal.Header>
          <Modal.Content>
            <p>
              {t('Say what was done in the CRM, or why nothing needs to be. The rows behind this one are delivered next.', ns)}
            </p>
            <Form>
              <Form.TextArea
                value={note}
                onChange={this.handleNote}
                maxLength={2000}
                placeholder={t('Note (required)', ns)}
                aria-label={t('Note (required)', ns)}
                data-testid="crm-sync-note"
              />
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={this.handleCloseResolve} disabled={acting !== null}>{t('Cancel', ns)}</Button>
            <Button
              primary
              onClick={this.handleResolve}
              disabled={!note.trim() || acting !== null || loading}
              loading={acting !== null}
              data-testid="crm-sync-resolve-confirm"
            >
              {t('Mark resolved', ns)}
            </Button>
          </Modal.Actions>
        </Modal>
      </div>
    );
    const needsAttention = (rows || []).some(row => ['needs_human', 'failed'].includes(row.status));
    const pending = (rows || []).some(isNonTerminal);
    const resolved = (rows || []).every(row => row.status === 'resolved');
    const sent = (rows || []).every(row => row.status === 'sent');
    const summary = error ? 'Unable to load' : needsAttention || actionError ? 'Needs attention' : pending ? 'Pending' : resolved ? 'Resolved' : sent ? 'Sent' : 'No pending events';
    // The tile's colour follows the shared rule (IntegrationStatusTile): attention for a parked row
    // or a failed action, waiting while anything is still moving, done when all is sent, closed
    // when everything was resolved by hand or there is nothing to say.
    let tone = 'closed';
    if (needsAttention || problem) tone = 'attention';
    else if (pending) tone = 'waiting';
    else if (sent) tone = 'done';
    const display = this.props.compact ? (
      <span className="crm-sync-summary">
        <IntegrationStatusTile
          name={t('CRM sync', ns)}
          state={t(summary, ns)}
          tone={tone}
          icon="exchange"
          onClick={() => this.setState({ historyOpen: true })}
          expanded={this.state.historyOpen}
          testId="crm-sync-open"
        />
        <Modal open={this.state.historyOpen} size="large" closeIcon
          closeOnDimmerClick={false}
          onClose={() => { if (!resolving && acting === null) this.setState({ historyOpen: false }); }}>
          <Modal.Header>{t('CRM sync', ns)}</Modal.Header>
          <Modal.Content scrolling>{content}</Modal.Content>
          <Modal.Actions><Button disabled={Boolean(resolving) || acting !== null}
            onClick={() => this.setState({ historyOpen: false })}>{t('Close', ns)}</Button></Modal.Actions>
        </Modal>
      </span>
    ) : content;
    return wrap ? wrap(display) : display;
  }
}

export default withTranslation(namespaces)(connect()(CrmSyncPanel));



// WEBPACK FOOTER //
// ./src/components/Crm/CrmSyncPanel.jsx